import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { X, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * SO allocation editor — read-only view of derived state.
 * All state (allocations, availableSOs, manualAlloc) is owned by the parent
 * (SPBCreate) so this component stays a controlled UI shell.
 *
 * Props:
 *   availableSOs, soAllocations   arrays
 *   totalAllocated, jumlahNum     numbers
 *   soShortfall, hasOverQuotaAlloc, availableSoTotals   derived values
 *   manualAlloc                   boolean
 *   satuan                        string ("SAK" | "TON")
 *   maxSoPerSpb                   number
 *   onResetAuto                   fn ()
 *   onUpdateQty                   fn (so_id, qty)
 *   onAddSo                       fn (so_id)
 *   onRemoveSo                    fn (so_id)
 */
export default function SoAllocationTable({
  availableSOs, soAllocations,
  totalAllocated, jumlahNum,
  soShortfall, hasOverQuotaAlloc, availableSoTotals,
  manualAlloc, satuan, maxSoPerSpb,
  onResetAuto, onUpdateQty, onAddSo, onRemoveSo,
}) {
  return (
    <div className="space-y-3" data-testid="so-section">
      <div className="flex justify-between items-start gap-3 pb-3 border-b border-border">
        <div>
          <div className="overline">Alokasi Sales Order (SO)</div>
          <p className="text-xs text-muted-foreground mt-1">
            Auto-fill FIFO — bisa edit qty, hapus, atau tambah SO manual (maks {maxSoPerSpb} SO per SPB).
          </p>
        </div>
        {manualAlloc && (
          <Button
            type="button" variant="outline" size="sm"
            className="rounded-none text-xs"
            onClick={onResetAuto}
            data-testid="reset-auto-fill-btn"
          >
            <ArrowsClockwise size={12} className="mr-1" />
            Reset ke Auto
          </Button>
        )}
      </div>
      {availableSOs.length === 0 ? (
        <div className="border border-dashed border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive" data-testid="so-empty-warn">
          Belum ada SO aktif untuk kombinasi plant + produk ini. Silakan buat SO dulu di Master Data → Sales Order.
        </div>
      ) : (
        <div className="border border-border bg-secondary/20 p-4">
          <div className="text-xs mb-2 text-muted-foreground">
            Total SO tersedia: <strong className="text-foreground">{availableSoTotals.healthy}</strong> {satuan}
            {availableSoTotals.overQuotaCount > 0 && (
              <span className="ml-2 text-destructive">
                · {availableSoTotals.overQuotaCount} SO over-quota tersedia
              </span>
            )}
          </div>
          {soAllocations.length > 0 ? (
            <table className="w-full text-sm" data-testid="so-allocation-table">
              <thead>
                <tr className="text-left border-b border-border">
                  <th className="py-2 overline">No SO</th>
                  <th className="py-2 overline">Qty Dipakai</th>
                  <th className="py-2 overline">Sisa Setelah</th>
                  <th className="py-2 overline w-10"></th>
                </tr>
              </thead>
              <tbody>
                {soAllocations.map((a) => {
                  const so = availableSOs.find((x) => x.id === a.so_id);
                  const remaining = so ? (so.kuantiti_sisa - a.qty) : 0;
                  const isOver = !!a.over_quota;
                  return (
                    <tr key={a.so_id} className={`border-b border-border/40 ${isOver ? "bg-destructive/5" : ""}`} data-testid={`so-alloc-row-${a.so_id}`}>
                      <td className="py-2 font-mono font-bold">
                        <div className="flex items-center gap-2">
                          {a.no_so}
                          {isOver && (
                            <span className="text-[9px] font-bold bg-destructive text-destructive-foreground px-1 py-0.5 uppercase tracking-wider">
                              Over Quota
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2">
                        <Input
                          type="number" min="0" step="0.001"
                          value={a.qty}
                          onChange={(e) => onUpdateQty(a.so_id, e.target.value)}
                          className="rounded-none h-8 w-28 font-mono text-sm"
                          data-testid={`so-alloc-qty-${a.so_id}`}
                        />
                      </td>
                      <td className={`py-2 font-mono text-xs ${remaining < 0 ? "text-destructive font-bold" : "text-muted-foreground"}`}>
                        {remaining.toFixed(3)} {satuan}
                      </td>
                      <td className="py-2 text-right">
                        <button
                          type="button"
                          onClick={() => onRemoveSo(a.so_id)}
                          className="text-muted-foreground hover:text-destructive p-1"
                          title="Hapus SO ini"
                          data-testid={`so-alloc-remove-${a.so_id}`}
                        >
                          <X size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-border font-bold">
                  <td className="py-2 overline text-xs">Total Alokasi</td>
                  <td className={`py-2 font-mono ${Math.abs(totalAllocated - jumlahNum) > 0.01 ? "text-destructive" : "text-foreground"}`}>
                    {totalAllocated.toFixed(3)} / {jumlahNum.toFixed(3)} {satuan}
                  </td>
                  <td colSpan="2"></td>
                </tr>
              </tbody>
            </table>
          ) : (
            <div className="text-sm text-muted-foreground">Masukkan jumlah untuk melihat alokasi.</div>
          )}

          {soAllocations.length < maxSoPerSpb && availableSOs.some(
            (so) => !soAllocations.find((a) => a.so_id === so.id),
          ) && (
            <div className="mt-3 pt-3 border-t border-border/40">
              <Label className="text-xs text-muted-foreground mb-1 block">
                Tambah SO ({soAllocations.length}/{maxSoPerSpb})
              </Label>
              <Select value="" onValueChange={onAddSo}>
                <SelectTrigger className="rounded-none h-9 text-sm" data-testid="add-so-picker">
                  <SelectValue placeholder="+ Pilih SO tambahan…" />
                </SelectTrigger>
                <SelectContent>
                  {availableSOs
                    .filter((so) => !soAllocations.find((a) => a.so_id === so.id))
                    .map((so) => (
                      <SelectItem key={so.id} value={so.id} className="font-mono">
                        <div className="flex justify-between gap-4 w-full">
                          <span className="font-bold">{so.no_so}</span>
                          <span className="text-xs text-muted-foreground">
                            sisa {so.kuantiti_sisa} {satuan}
                            {so.is_over_quota && " · OVER"}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {soAllocations.length >= maxSoPerSpb && (
            <div className="mt-3 pt-3 border-t border-border/40 text-xs text-muted-foreground">
              Maksimal {maxSoPerSpb} SO per SPB tercapai. Hapus salah satu untuk menambah SO lain.
            </div>
          )}

          {hasOverQuotaAlloc && (
            <div className="mt-3 p-2 bg-destructive/10 border-l-4 border-destructive text-xs text-destructive" data-testid="so-over-quota-warn">
              ⚠ Alokasi ini menggunakan SO yang sudah melebihi kuota. Anda akan diminta konfirmasi saat menyimpan.
            </div>
          )}
          {soShortfall > 0 && (
            <div className="mt-3 p-2 bg-destructive/10 border border-destructive/40 text-xs text-destructive" data-testid="so-shortfall-warn">
              ⚠ Total alokasi kurang <strong>{soShortfall.toFixed(3)} {satuan}</strong> dari jumlah SPB. Tambah SO atau sesuaikan qty.
            </div>
          )}
          {totalAllocated - jumlahNum > 0.01 && (
            <div className="mt-3 p-2 bg-destructive/10 border border-destructive/40 text-xs text-destructive" data-testid="so-overflow-warn">
              ⚠ Total alokasi melebihi jumlah SPB sebesar <strong>{(totalAllocated - jumlahNum).toFixed(3)} {satuan}</strong>. Kurangi qty pada salah satu SO.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
